<template>
    <div class="a-drawer" tabindex="0" @keydown.esc="close()">
        <div
            v-if="open && mask"
            class="a-drawer-mask"
            :style="{ 'z-index': zIndex }"
            @click="closeOnOutsideClick"
        ></div>
        <transition :name="transitionName" appear>
            <div
                v-if="open"
                ref="drawer"
                class="a-drawer-container"
                :style="containerStyles"
            >
                <div
                    class="a-drawer-holder elevation-4"
                    tabindex="0"
                    :style="styles"
                    @keydown.esc="close()"
                    @click.stop
                >
                    <AonContainer>
                        <AonRow>
                            <AonCol class="aon-col-12 pa-0">
                                <AonButton
                                    v-if="showClose"
                                    class="a-close"
                                    :class="{ end: right }"
                                    type="icon"
                                    :style="{ color: '#000' }"
                                    :icon-options="{
                                        iconStyle: 'fas',
                                        iconName: 'fa-xmark',
                                    }"
                                    @click="close()"
                                />
                                <slot name="headerAppend"></slot>
                            </AonCol>
                        </AonRow>
                    </AonContainer>
                    <slot></slot>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import AonContainer from './grid/AonContainer.vue';
import AonRow from './grid/AonRow.vue';
import AonCol from './grid/AonCol.vue';
import AonButton from '../base/AonButton.vue';

const props = defineProps({
    left: {
        type: Boolean,
        default: false,
    },
    right: {
        type: Boolean,
        default: false,
    },
    persistent: {
        type: Boolean,
        default: false,
    },
    drawerOpen: {
        type: Boolean,
        required: true,
    },
    width: {
        type: [String, Number],
        default: '50vw',
    },
    mask: {
        type: Boolean,
        default: true,
    },
    // CSS unit is 'px' only for now
    topOffset: {
        type: Number,
        default: 0,
    },
    showClose: {
        type: Boolean,
        default: true,
    },
    zIndex: {
        type: Number,
        default: 10005,
    },
});
const emit = defineEmits(['input']);

const drawer = ref(null);
const calcWidth = computed(() => {
    return typeof props.width === 'number' ? `${props.width}px` : props.width;
});

const open = computed({
    get() {
        return props.drawerOpen;
    },
    set(v) {
        if (v) {
            focus();
        } else {
            emit('input', v);
        }
    },
});

const styles = computed(() => {
    const styles = {
        width: calcWidth.value,
        height: !!props.topOffset
            ? `calc(100vh - ${props.topOffset}px)`
            : '100vh',
    };

    return styles;
});

const containerStyles = computed(() => {
    const styles = {
        top: !!props.topOffset ? `${props.topOffset}px` : props.topOffset,
        width: calcWidth.value,
        'z-index': props.zIndex + 1,
    };

    if (props.left) {
        styles['left'] = 0;
    }

    if (props.right) {
        styles['right'] = 0;
    }
    return styles;
});

const transitionName = computed(() => {
    if (props.right) {
        return 'a-slide-left';
    }
    return 'a-slide-right';
});

watch(
    () => props.drawerOpen,
    (newVal) => {
        open.value = newVal;
    }
);

const close = () => {
    open.value = false;
};
const closeOnOutsideClick = () => {
    if (props.persistent) {
        return;
    }
    close();
};
const focus = async () => {
    await nextTick();
    drawer.value.focus();
};
</script>

<script>
export default {
    name: 'AonDrawer',
    components: ['AonButton'],
};
</script>

<style lang="scss">
.a-drawer {
    .a-drawer-mask {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        background-color: rgba(1, 39, 116, 0.24);
    }

    .a-drawer-container {
        position: fixed;
        bottom: 0;

        .a-drawer-holder {
            padding: 1em;
            overflow-y: auto;
            border-left: 1px solid $grey06;
            outline: none;
            background: white;
        }
    }

    .a-close {
        display: flex;
        // TODO fix: not working with the headerAppend slot populated
        &.end {
            justify-content: end;
        }
    }

    .a-slide-left-enter-active {
        transform-origin: right;
        animation: aSlideIn 0.3s ease-in-out;
    }

    .a-slide-left-leave-active {
        transform-origin: right;
        animation: aSlideOut 0.4s ease-in-out;
    }

    .a-slide-right-enter-active {
        transform-origin: left;
        animation: aSlideIn 0.3s ease-in-out;
    }

    .a-slide-right-leave-active {
        transform-origin: left;
        animation: aSlideOut 0.4s ease-in-out;
    }

    @keyframes aSlideIn {
        0% {
            transform: scaleX(0);
        }

        100% {
            transform: scaleX(1);
        }
    }

    @keyframes aSlideOut {
        0% {
            transform: scaleX(1);
        }

        100% {
            transform: scaleX(0);
        }
    }
}
</style>
